// Dải đầu trang Tổng quan (Shop / CTV / Đại lý): cảnh báo, khuyến mãi cấp trên gửi — còn hạn, chưa bấm "Ẩn",
// tối đa 2 dải (máy chủ lọc: GET /notifications/banners). Không có thì không hiện gì.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ANNOUNCE_KIND, announceKind, type AnnouncementContent } from './AnnouncementView'
import { AnnouncementSheet, refreshNotifications } from './AnnouncementSheet'

interface BannerItem {
  id: string
  kind: string
  title: string
  message: string
  sender?: string | null
  url?: string | null
  expiresAt?: string | null
  isRead: boolean
  createdAt: string
}

const BORDER = { warning: 'border-danger/30', promo: 'border-ok/30', info: 'border-info/30' } as const

export function AnnouncementBanners({ className }: { className?: string }) {
  const qc = useQueryClient()
  const [reading, setReading] = useState<AnnouncementContent | null>(null)
  const q = useQuery({
    queryKey: ['notifications-banners'],
    queryFn: () => get<{ items: BannerItem[] }>('/notifications/banners'),
    staleTime: 60_000,
    retry: false,
  })
  const markRead = useMutation({ mutationFn: (id: string) => post(`/notifications/${id}/read`), onSuccess: () => refreshNotifications(qc) })
  const dismiss = useMutation({
    mutationFn: (id: string) => post(`/notifications/${id}/dismiss`),
    onSuccess: (_r, id) => {
      qc.setQueryData<{ items: BannerItem[] }>(['notifications-banners'], (old) => (old ? { ...old, items: (old.items ?? []).filter((x) => x.id !== id) } : old))
      refreshNotifications(qc)
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Chưa ẩn được'),
  })

  const items = Array.isArray(q.data?.items) ? q.data.items.slice(0, 2) : []

  return (
    <>
      {items.length > 0 && (
        <div className={cn('space-y-2', className)}>
          {items.map((b) => {
            const kind = announceKind(b.kind)
            const k = ANNOUNCE_KIND[kind]
            const hiding = dismiss.isPending && dismiss.variables === b.id
            return (
              // Thẻ bg-raise + lớp màu nhạt bên trong (overflow-hidden cắt theo góc thẻ): đọc được cả trên nền kính (Glass,
              // glass.css đổi bo góc / viền của thẻ) lẫn nền giấy
              <section key={b.id} aria-label={k.label} className={cn('overflow-hidden rounded-xl border bg-raise shadow-card', BORDER[kind])}>
                <div className={cn('flex items-start gap-3 px-4 py-3', k.band)}>
                  <k.icon className={cn('mt-0.5 size-5 shrink-0', k.text)} aria-hidden />
                  <div className="min-w-0 flex-1 desktop:flex desktop:items-center desktop:gap-4">
                    <div className="min-w-0 flex-1">
                      <h2 className="break-words text-sm font-semibold text-ink">{b.title}</h2>
                      <p className="mt-0.5 line-clamp-2 whitespace-pre-line break-words text-sm text-inkSoft">{b.message}</p>
                      {b.sender && <p className="mt-1 text-xs font-medium text-muted">{b.sender}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 mobile:mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-raise"
                        onClick={() => {
                          if (!b.isRead) markRead.mutate(b.id)
                          setReading({ kind, title: b.title ?? '', message: b.message ?? '', sender: b.sender ?? null, createdAt: b.createdAt, url: b.url ?? null })
                        }}
                      >
                        Xem
                      </Button>
                      <Button size="sm" variant="ghost" disabled={hiding} onClick={() => dismiss.mutate(b.id)}>
                        Ẩn
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            )
          })}
        </div>
      )}
      <AnnouncementSheet a={reading} onClose={() => setReading(null)} />
    </>
  )
}
